package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.KioskDevice
import com.evoapps.evoshift.domain.model.KioskPairingCode
import com.evoapps.evoshift.domain.repository.KioskRepository
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.kiosk_error_pin_shape
import evoshift.composeapp.generated.resources.kiosk_pin_mismatch
import evoshift.composeapp.generated.resources.kiosk_pin_removed
import evoshift.composeapp.generated.resources.kiosk_pin_saved
import evoshift.composeapp.generated.resources.kiosk_revoked_done
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

// --- The tablet's sign-in screen: "Pair as a clock-in tablet" ----------------------------------

data class KioskPairUiState(
    val code: String = "",
    val name: String = "",
    val isPairing: Boolean = false,
    val error: UiText? = null
) {
    /** 8 characters without dashes and spaces. */
    val canPair: Boolean get() = code.count { it.isLetterOrDigit() } == 8 && !isPairing
}

/** SH-107: pairs this device with the code a manager shows; the session then turns it into the kiosk. */
class KioskPairViewModel(private val repository: KioskRepository) : ViewModel() {
    private val _uiState = MutableStateFlow(KioskPairUiState())
    val uiState: StateFlow<KioskPairUiState> = _uiState.asStateFlow()

    fun code(value: String) = _uiState.update { it.copy(code = value.uppercase().take(12), error = null) }

    fun name(value: String) = _uiState.update { it.copy(name = value.take(60)) }

    fun pair() {
        val state = _uiState.value
        if (!state.canPair) return
        _uiState.update { it.copy(isPairing = true, error = null) }
        viewModelScope.launch {
            repository.pair(state.code, state.name.trim().ifEmpty { null }).fold(
                // The session follows the sign-in: App.kt shows the kiosk.
                onSuccess = { _uiState.update { it.copy(isPairing = false) } },
                onFailure = { e -> _uiState.update { it.copy(isPairing = false, error = e.toUiText()) } }
            )
        }
    }
}

// --- Workplace settings: the workplace's tablets ------------------------------------------------

data class KioskDevicesUiState(
    val devices: List<KioskDevice> = emptyList(),
    val isLoading: Boolean = true,
    /** The name typed for the next tablet. */
    val name: String = "",
    val code: KioskPairingCode? = null,
    val isCreatingCode: Boolean = false,
    val revoking: KioskDevice? = null,
    /** SH-111: whether the tablets show the rotating QR (settings.kioskQrEnabled, default on). */
    val qrEnabled: Boolean = true,
    val error: UiText? = null,
    val message: UiText? = null
)

/** SH-107: managers pair tablets (a one-time code), see when each was last used, and unpair them. */
class KioskDevicesViewModel(
    private val workplaceId: String,
    private val repository: KioskRepository
) : ViewModel() {
    private val _uiState = MutableStateFlow(KioskDevicesUiState())
    val uiState: StateFlow<KioskDevicesUiState> = _uiState.asStateFlow()

    init {
        repository.observeDevices(workplaceId)
            .onEach { devices -> _uiState.update { it.copy(devices = devices, isLoading = false) } }
            .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
            .launchIn(viewModelScope)
        repository.observeQrEnabled(workplaceId)
            .onEach { enabled -> _uiState.update { it.copy(qrEnabled = enabled) } }
            .catch { e -> _uiState.update { it.copy(error = e.toUiText()) } }
            .launchIn(viewModelScope)
    }

    /** SH-111, optimistic: the switch moves at once and back if the write fails. */
    fun setQrEnabled(enabled: Boolean) {
        val before = _uiState.value.qrEnabled
        if (before == enabled) return
        _uiState.update { it.copy(qrEnabled = enabled) }
        viewModelScope.launch {
            repository.setQrEnabled(workplaceId, enabled).onFailure { e ->
                _uiState.update { it.copy(qrEnabled = before, error = e.toUiText()) }
            }
        }
    }

    fun name(value: String) = _uiState.update { it.copy(name = value.take(60)) }

    fun createCode() {
        if (_uiState.value.isCreatingCode) return
        _uiState.update { it.copy(isCreatingCode = true, error = null) }
        viewModelScope.launch {
            repository.createPairingCode(workplaceId, _uiState.value.name.trim().ifEmpty { null }).fold(
                onSuccess = { code -> _uiState.update { it.copy(code = code, isCreatingCode = false, name = "") } },
                onFailure = { e -> _uiState.update { it.copy(isCreatingCode = false, error = e.toUiText()) } }
            )
        }
    }

    fun closeCode() = _uiState.update { it.copy(code = null) }

    fun askRevoke(device: KioskDevice) = _uiState.update { it.copy(revoking = device) }

    fun cancelRevoke() = _uiState.update { it.copy(revoking = null) }

    /** Optimistic: shown unpaired at once, back as it was if the server refuses. */
    fun confirmRevoke() {
        val device = _uiState.value.revoking ?: return
        val before = _uiState.value.devices
        _uiState.update { s -> s.copy(revoking = null, devices = s.devices.map { if (it.id == device.id) it.copy(revoked = true) else it }) }
        viewModelScope.launch {
            repository.revoke(workplaceId, device.id).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.kiosk_revoked_done)) } },
                onFailure = { e -> _uiState.update { it.copy(devices = before, error = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(error = null, message = null) }
}

// --- Profile: the person's tablet PIN -----------------------------------------------------------

data class KioskPinUiState(
    /** Null while unknown (loading, or offline). */
    val isSet: Boolean? = null,
    val pin: String = "",
    val repeat: String = "",
    val isSaving: Boolean = false,
    val error: UiText? = null,
    val message: UiText? = null,
    /** The PIN was saved or removed: the dialog closes. */
    val done: Boolean = false
) {
    val canSave: Boolean get() = pin.length == KioskViewModel.PIN_LENGTH && repeat.isNotEmpty() && !isSaving
}

/** SH-107: each person sets their own 6-digit PIN (the server keeps only a salted hash). */
class KioskPinViewModel(private val repository: KioskRepository) : ViewModel() {
    private val _uiState = MutableStateFlow(KioskPinUiState())
    val uiState: StateFlow<KioskPinUiState> = _uiState.asStateFlow()

    init {
        refresh()
    }

    fun refresh() {
        viewModelScope.launch {
            repository.pinStatus().onSuccess { set -> _uiState.update { it.copy(isSet = set) } }
        }
    }

    fun pin(value: String) = _uiState.update { it.copy(pin = value.filter(Char::isDigit).take(6), error = null, done = false) }

    fun repeat(value: String) = _uiState.update { it.copy(repeat = value.filter(Char::isDigit).take(6), error = null, done = false) }

    fun save() {
        val state = _uiState.value
        if (!state.canSave) return
        when {
            state.pin.length != KioskViewModel.PIN_LENGTH -> return _uiState.update { it.copy(error = UiText.Resource(Res.string.kiosk_error_pin_shape)) }
            state.pin != state.repeat -> return _uiState.update { it.copy(error = UiText.Resource(Res.string.kiosk_pin_mismatch)) }
        }
        write(state.pin, Res.string.kiosk_pin_saved)
    }

    fun remove() = write(null, Res.string.kiosk_pin_removed)

    private fun write(pin: String?, done: org.jetbrains.compose.resources.StringResource) {
        _uiState.update { it.copy(isSaving = true, error = null) }
        viewModelScope.launch {
            repository.setPin(pin).fold(
                onSuccess = {
                    _uiState.update {
                        KioskPinUiState(isSet = pin != null, message = UiText.Resource(done), done = true)
                    }
                },
                // The digits stay so the person can retry.
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null, done = false) }
}
